import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Activity,
  AlertTriangle,
  Camera,
  Check,
  ChevronLeft,
  ChevronRight,
  Droplets,
  Loader2,
  MapPin,
  MessageSquare,
  Plus,
  RefreshCcw,
  Send,
  ShoppingCart,
  Sparkles,
  Square,
  ThermometerSun,
  Trash2,
  Upload,
  Video,
  Wallet,
  Zap,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Switch } from '../../components/ui/switch';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { cn } from '../../lib/utils';
import { getEvidenceItemsForField, normalizeEvidenceItems } from '../../lib/evidenceUtils';
import { getVisitAddress, hasGpsCheckIn } from '../../lib/visitUtils';
import { getApiErrorMessage } from '../../lib/apiError';

const STEPS = [
  { id: 'vitals', title: 'Vitals', icon: ThermometerSun },
  { id: 'activity', title: 'Physical Activity', icon: Activity },
  { id: 'sustenance', title: 'Sustenance', icon: ShoppingCart },
  { id: 'utilities', title: 'Utilities', icon: Zap },
  { id: 'observations', title: 'Observations', icon: MessageSquare },
  { id: 'expenses', title: 'Care Expenses', icon: Wallet },
  { id: 'review', title: 'Review & Submit', icon: Send },
];

const DEFAULT_SCORE_OPTIONS = ['Poor', 'Fair', 'Good', 'Great'];
const DEFAULT_SPEECH_OPTIONS = ['Weak', 'Fair', 'Strong'];
const DEFAULT_SUSTENANCE_OPTIONS = ['Adequate', 'Inadequate', 'Restock Immediately'];
const DEFAULT_WATER_OPTIONS = ['Adequate', 'Inadequate', 'Requires Attention'];
const DEFAULT_POWER_OPTIONS = ['Adequate', 'Inadequate (<72hrs)', 'Requires Immediate Top-Up'];
const DEFAULT_SANITATION_OPTIONS = ['Good', 'Fair', 'Poor', 'Requires Attention'];

const emptyFormData = {
  vitals: {
    bodyTemperature: '',
    bloodPressureSystolic: '',
    bloodPressureDiastolic: '',
    physicalAppearance: '',
  },
  activity: {
    abilityToStandScore: '',
    abilityToStandAssistance: false,
    abilityToWalkScore: '',
    abilityToWalkAssistance: false,
    speechScore: '',
  },
  sustenance: {
    foodScore: '',
    groceriesScore: '',
    drinkingWaterScore: '',
  },
  utilities: {
    powerCreditScore: '',
    environmentalSanitationScore: '',
  },
  observations: {
    otherObservations: '',
    generalComments: '',
  },
};

const checklistToFormData = (checklist = {}) => ({
  vitals: {
    bodyTemperature: checklist.vitals?.bodyTemperature?.value ?? checklist.vitals?.body_temperature?.value ?? '',
    bloodPressureSystolic: checklist.vitals?.bloodPressure?.systolic ?? checklist.vitals?.blood_pressure?.systolic ?? '',
    bloodPressureDiastolic: checklist.vitals?.bloodPressure?.diastolic ?? checklist.vitals?.blood_pressure?.diastolic ?? '',
    physicalAppearance: checklist.vitals?.physicalAppearance?.score ?? checklist.vitals?.physical_appearance?.score ?? '',
  },
  activity: {
    abilityToStandScore: checklist.physicalActivity?.abilityToStand?.score ?? checklist.physical_activity?.ability_to_stand?.score ?? '',
    abilityToStandAssistance: Boolean(checklist.physicalActivity?.abilityToStand?.assistanceRequired ?? checklist.physical_activity?.ability_to_stand?.assistance_required),
    abilityToWalkScore: checklist.physicalActivity?.abilityToWalk?.score ?? checklist.physical_activity?.ability_to_walk?.score ?? '',
    abilityToWalkAssistance: Boolean(checklist.physicalActivity?.abilityToWalk?.assistanceRequired ?? checklist.physical_activity?.ability_to_walk?.assistance_required),
    speechScore: checklist.physicalActivity?.speech?.score ?? checklist.physical_activity?.speech?.score ?? '',
  },
  sustenance: {
    foodScore: checklist.sustenance?.food?.score ?? '',
    groceriesScore: checklist.sustenance?.groceries?.score ?? '',
    drinkingWaterScore: checklist.sustenance?.drinkingWater?.score ?? checklist.sustenance?.drinking_water?.score ?? '',
  },
  utilities: {
    powerCreditScore: checklist.utilities?.powerCredit?.score ?? checklist.utilities?.power_credit?.score ?? '',
    environmentalSanitationScore: checklist.utilities?.environmentalSanitation?.score ?? checklist.utilities?.environmental_sanitation?.score ?? '',
  },
  observations: {
    otherObservations: checklist.otherObservations ?? checklist.other_observations ?? '',
    generalComments: checklist.generalComments ?? checklist.general_comments ?? '',
  },
});

const toNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isNaN(number) ? null : number;
};

const formatAddress = (dependant) => {
  const address = dependant?.address;
  if (!address) return 'Address not available';
  return [address.street, address.city, address.state || address.lga, address.country].filter(Boolean).join(', ');
};

const getCurrentPosition = (required = true) => {
  if (!navigator.geolocation) {
    return required
      ? Promise.reject(new Error('GPS is not available on this device or browser.'))
      : Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date(position.timestamp).toISOString(),
        });
      },
      (error) => {
        if (!required) {
          resolve(null);
          return;
        }
        const message = error.code === error.PERMISSION_DENIED
          ? 'GPS permission is required to complete a visit.'
          : 'Unable to get GPS location. Move to an open area and try again.';
        reject(new Error(message));
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  });
};

const EvidenceCapture = ({ authApi, visitId, requirement, evidence, evidenceItems = [], onUploaded }) => {
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingStartedAtRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const evidenceType = requirement?.evidenceType || 'photo';
  const isVideo = evidenceType === 'video';
  const allEvidenceItems = normalizeEvidenceItems(evidence, evidenceItems);
  const latestEvidence = allEvidenceItems[allEvidenceItems.length - 1] || null;
  const previewUrl = latestEvidence?.localPreviewUrl || latestEvidence?.fileUrl;

  const stopCamera = useCallback(() => {
    setRecording(false);
    setCameraOpen(false);
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    setStream(null);
  }, [stream]);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  useEffect(() => () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
  }, [stream]);

  const uploadEvidence = async (file, durationSeconds = null) => {
    setUploading(true);
    setError('');
    try {
      const gps = await getCurrentPosition(false);
      const payload = new FormData();
      payload.append('field_name', requirement.fieldName);
      payload.append('evidence_type', evidenceType);
      payload.append('captured_at', new Date().toISOString());
      payload.append('file', file);
      if (durationSeconds !== null) {
        payload.append('duration_seconds', String(durationSeconds));
      }
      if (gps) {
        payload.append('latitude', String(gps.latitude));
        payload.append('longitude', String(gps.longitude));
        if (gps.accuracy !== undefined && gps.accuracy !== null) {
          payload.append('accuracy', String(gps.accuracy));
        }
      }

      const response = await authApi.post(`/visits/${visitId}/evidence`, payload);
      const localPreviewUrl = URL.createObjectURL(file);
      onUploaded(requirement.fieldName, {
        ...response.data.evidence,
        localPreviewUrl,
      }, response.data.visit);
      toast.success(`${requirement.label} uploaded`);
      stopCamera();
    } catch (err) {
      const message = getApiErrorMessage(err, 'Unable to upload evidence. Please try again.');
      setError(message);
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    files.forEach((file) => uploadEvidence(file));
  };

  const openCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      fileInputRef.current?.click();
      return;
    }
    setError('');
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: isVideo,
      });
      setStream(mediaStream);
      setCameraOpen(true);
    } catch (err) {
      setError('Camera access failed. Use the upload control or check browser permissions.');
      fileInputRef.current?.click();
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      setError('Camera is still starting. Try again in a moment.');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) {
        setError('Unable to capture photo from camera.');
        return;
      }
      const file = new File([blob], `${requirement.fieldName}-${Date.now()}.jpg`, { type: 'image/jpeg' });
      uploadEvidence(file);
    }, 'image/jpeg', 0.92);
  };

  const startRecording = () => {
    if (!stream || typeof MediaRecorder === 'undefined') {
      setError('Video recording is not supported in this browser. Use the file capture control.');
      fileInputRef.current?.click();
      return;
    }
    chunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported('video/webm') ? 'video/webm' : '';
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    recorderRef.current = recorder;
    recordingStartedAtRef.current = Date.now();
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType || 'video/webm' });
      const durationSeconds = recordingStartedAtRef.current
        ? Math.round((Date.now() - recordingStartedAtRef.current) / 1000)
        : null;
      const file = new File([blob], `${requirement.fieldName}-${Date.now()}.webm`, { type: blob.type });
      uploadEvidence(file, durationSeconds);
    };
    recorder.start();
    setRecording(true);
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    setRecording(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <Label className="text-sm font-medium">{requirement.label} *</Label>
        <Badge variant="outline">{isVideo ? 'Video required' : 'Photo required'}</Badge>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={requirement.accept}
        capture="environment"
        multiple={!isVideo}
        className="hidden"
        onChange={handleFileChange}
      />

      <div className={cn(
        'overflow-hidden rounded-xl border-2 border-dashed',
        allEvidenceItems.length ? 'border-green-500 bg-green-50' : 'border-border bg-muted/30'
      )}>
        {previewUrl ? (
          isVideo ? (
            <video src={previewUrl} controls className="h-56 w-full bg-black object-contain" />
          ) : (
            <img src={previewUrl} alt={requirement.label} className="h-56 w-full object-cover" />
          )
        ) : cameraOpen ? (
          <video ref={videoRef} autoPlay playsInline muted className="h-56 w-full bg-black object-contain" />
        ) : (
          <button
            type="button"
            onClick={openCamera}
            className="flex min-h-[180px] w-full flex-col items-center justify-center gap-2 p-6 text-center"
          >
            {isVideo ? <Video className="h-9 w-9 text-muted-foreground" /> : <Camera className="h-9 w-9 text-muted-foreground" />}
            <span className="text-sm font-medium text-muted-foreground">
              {isVideo ? 'Record or select video evidence' : 'Take or select photo evidence'}
            </span>
            <span className="text-xs text-destructive">Required *</span>
          </button>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t bg-white p-3">
          {allEvidenceItems.length ? (
            <span className="inline-flex items-center gap-2 text-sm font-medium text-green-700">
              <Check className="h-4 w-4" />
              {allEvidenceItems.length} evidence item{allEvidenceItems.length === 1 ? '' : 's'} uploaded
            </span>
          ) : (
            <span className="text-sm text-gray-500">GPS is attached when available.</span>
          )}

          <div className="flex flex-wrap gap-2">
            {cameraOpen && !isVideo && (
              <Button type="button" size="sm" onClick={capturePhoto} disabled={uploading}>
                <Camera className="mr-2 h-4 w-4" />
                Capture
              </Button>
            )}
            {cameraOpen && isVideo && !recording && (
              <Button type="button" size="sm" onClick={startRecording} disabled={uploading}>
                <Video className="mr-2 h-4 w-4" />
                Record
              </Button>
            )}
            {cameraOpen && isVideo && recording && (
              <Button type="button" size="sm" variant="destructive" onClick={stopRecording}>
                <Square className="mr-2 h-4 w-4" />
                Stop
              </Button>
            )}
            {cameraOpen && (
              <Button type="button" size="sm" variant="outline" onClick={stopCamera} disabled={uploading || recording}>
                Close
              </Button>
            )}
            <Button type="button" size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              {allEvidenceItems.length ? (isVideo ? 'Replace' : 'Add more') : 'Choose'}
            </Button>
          </div>
        </div>
      </div>

      {allEvidenceItems.length > 1 && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {allEvidenceItems.map((item, index) => {
            const rawUrl = item.localPreviewUrl || item.fileUrl;
            const previewSource = rawUrl && !String(rawUrl).startsWith('/api') ? rawUrl : item.localPreviewUrl;
            return (
              <div key={item.id || index} className="overflow-hidden rounded-lg border border-[#E8E6E3] bg-white">
                {previewSource ? (
                  item.evidenceType === 'video' || item.evidence_type === 'video' ? (
                    <video src={previewSource} className="h-24 w-full bg-black object-cover" muted playsInline />
                  ) : (
                    <img src={previewSource} alt={`${requirement.label} ${index + 1}`} className="h-24 w-full object-cover" />
                  )
                ) : (
                  <div className="flex h-24 items-center justify-center bg-[#F7F4EE] text-xs text-[#5A5A5A]">Uploaded</div>
                )}
                <p className="truncate px-2 py-1 text-xs text-[#5A5A5A]">{item.fileName || `Evidence ${index + 1}`}</p>
              </div>
            );
          })}
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
};

const VisitChecklistWizard = () => {
  const navigate = useNavigate();
  const { visitId } = useParams();
  const [searchParams] = useSearchParams();
  const { authApi } = useAuth();
  const { upsertVisit } = useApp();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(emptyFormData);
  const [evidence, setEvidence] = useState({});
  const [evidenceGroups, setEvidenceGroups] = useState({});
  const [schema, setSchema] = useState(null);
  const [visit, setVisit] = useState(null);
  const [dependant, setDependant] = useState(null);
  const [subscriber, setSubscriber] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [careExpenses, setCareExpenses] = useState([]);
  const [correctionResponse, setCorrectionResponse] = useState('');

  const loadChecklist = useCallback(async () => {
    if (!visitId) {
      navigate('/schedule', { replace: true });
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await authApi.get(`/visits/${visitId}/checklist-schema`);
      setSchema(response.data);
      setVisit(response.data.visit);
      setDependant(response.data.dependant);
      setSubscriber(response.data.subscriber);
      setEvidence(response.data.visit?.evidence || {});
      setEvidenceGroups(response.data.visit?.evidenceGroups || response.data.visit?.evidence_groups || {});
      if (response.data.visit?.checklist) {
        setFormData(checklistToFormData(response.data.visit.checklist));
      }
      setCorrectionResponse(response.data.visit?.latestOfficerResponse?.comments || response.data.visit?.latest_officer_response?.comments || '');
    } catch (err) {
      const message = getApiErrorMessage(err, 'Unable to load this visit checklist.');
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [authApi, navigate, visitId]);

  useEffect(() => {
    loadChecklist();
  }, [loadChecklist]);

  const scoreOptions = schema?.scoreOptions || {};
  const evidenceRequirements = schema?.evidenceRequirements || {};
  const physicalRequirement = evidenceRequirements.physicalCondition || {
    fieldName: 'physicalCondition',
    label: 'Physical condition evidence',
    evidenceType: 'photo',
    accept: 'image/*',
  };
  const foodRequirement = evidenceRequirements.food || {
    fieldName: 'food',
    label: 'Food evidence',
    evidenceType: 'photo',
    accept: 'image/*',
  };
  const groceriesRequirement = evidenceRequirements.groceries || {
    fieldName: 'groceries',
    label: 'Groceries evidence',
    evidenceType: 'photo',
    accept: 'image/*',
  };
  const drinkingWaterRequirement = evidenceRequirements.drinkingWater || {
    fieldName: 'drinkingWater',
    label: 'Drinking water evidence',
    evidenceType: 'photo',
    accept: 'image/*',
  };

  const suggestedExpenses = useMemo(() => {
    const suggestions = [];
    const add = ({ sourceField, sourceLabel, triggerValue, category, title, explanation, urgency = 'Normal' }) => {
      if (!triggerValue) return;
      const normalized = String(triggerValue).toLowerCase();
      if (normalized === 'adequate' || normalized === 'good') return;
      if (!['inadequate', 'restock', 'requires', 'top-up', 'poor'].some(token => normalized.includes(token))) return;
      suggestions.push({ sourceField, sourceLabel, triggerValue, category, title, explanation, urgency });
    };

    add({
      sourceField: 'sustenance.food',
      sourceLabel: 'Food availability',
      triggerValue: formData.sustenance.foodScore,
      category: 'Food',
      title: 'Food support',
      explanation: `Food availability was marked ${formData.sustenance.foodScore}.`,
      urgency: formData.sustenance.foodScore === 'Restock Immediately' ? 'Urgent' : 'Normal',
    });
    add({
      sourceField: 'sustenance.groceries',
      sourceLabel: 'Groceries',
      triggerValue: formData.sustenance.groceriesScore,
      category: 'Groceries',
      title: 'Grocery restock',
      explanation: `Groceries were marked ${formData.sustenance.groceriesScore}.`,
      urgency: formData.sustenance.groceriesScore === 'Restock Immediately' ? 'Urgent' : 'Normal',
    });
    add({
      sourceField: 'sustenance.drinking_water',
      sourceLabel: 'Drinking water',
      triggerValue: formData.sustenance.drinkingWaterScore,
      category: 'Water',
      title: 'Drinking water support',
      explanation: `Drinking water was marked ${formData.sustenance.drinkingWaterScore}.`,
      urgency: formData.sustenance.drinkingWaterScore === 'Requires Attention' ? 'Urgent' : 'Normal',
    });
    add({
      sourceField: 'utilities.power_credit',
      sourceLabel: 'Power credit',
      triggerValue: formData.utilities.powerCreditScore,
      category: 'Power',
      title: 'Power top-up',
      explanation: `Power credit was marked ${formData.utilities.powerCreditScore}.`,
      urgency: formData.utilities.powerCreditScore === 'Requires Immediate Top-Up' ? 'Urgent' : 'Normal',
    });
    add({
      sourceField: 'utilities.environmental_sanitation',
      sourceLabel: 'Environmental sanitation',
      triggerValue: formData.utilities.environmentalSanitationScore,
      category: 'Sanitation',
      title: 'Sanitation support',
      explanation: `Environmental sanitation was marked ${formData.utilities.environmentalSanitationScore}.`,
      urgency: formData.utilities.environmentalSanitationScore === 'Requires Attention' ? 'Urgent' : 'Normal',
    });
    return suggestions;
  }, [formData.sustenance, formData.utilities]);

  useEffect(() => {
    setCareExpenses(prev => {
      const suggestedSources = new Set(suggestedExpenses.map(item => item.sourceField));
      const retained = prev.filter(item => !item.fromSuggestion || suggestedSources.has(item.sourceField));
      const existingSources = new Set(retained.map(item => item.sourceField).filter(Boolean));
      const additions = suggestedExpenses
        .filter(item => !existingSources.has(item.sourceField))
        .map(item => ({
          id: `suggested-${item.sourceField}`,
          ...item,
          estimatedAmount: '',
          currency: 'NGN',
          fromSuggestion: true,
        }));
      return additions.length ? [...retained, ...additions] : retained;
    });
  }, [suggestedExpenses]);

  const updateFormData = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleEvidenceUploaded = (fieldName, reference, updatedVisit) => {
    setEvidence(prev => ({ ...prev, [fieldName]: reference }));
    setEvidenceGroups(prev => ({
      ...prev,
      [fieldName]: normalizeEvidenceItems(reference, [
        ...(prev[fieldName] || []),
        reference,
      ]),
    }));
    if (updatedVisit) {
      setVisit(updatedVisit);
      setEvidence(updatedVisit.evidence || {});
      setEvidenceGroups(updatedVisit.evidenceGroups || updatedVisit.evidence_groups || {});
      upsertVisit(updatedVisit);
    }
  };

  const updateCareExpense = (id, key, value) => {
    setCareExpenses(prev => prev.map(item => item.id === id ? { ...item, [key]: value, fromSuggestion: false } : item));
  };

  const addCareExpense = () => {
    setCareExpenses(prev => [
      ...prev,
      {
        id: `custom-${Date.now()}`,
        title: '',
        category: 'Other',
        explanation: '',
        estimatedAmount: '',
        currency: 'NGN',
        urgency: 'Normal',
        fromSuggestion: false,
      },
    ]);
  };

  const removeCareExpense = (id) => {
    setCareExpenses(prev => prev.filter(item => item.id !== id));
  };

  const isStepComplete = (stepIndex) => {
    const step = STEPS[stepIndex];
    const data = formData[step.id];

    switch (step.id) {
      case 'vitals':
        return data.bodyTemperature &&
          data.bloodPressureSystolic &&
          data.bloodPressureDiastolic &&
          data.physicalAppearance &&
          getEvidenceItemsForField({ evidence, evidenceGroups }, 'physicalCondition').length > 0;
      case 'activity':
        return data.abilityToStandScore && data.abilityToWalkScore && data.speechScore;
      case 'sustenance':
        return data.foodScore &&
          getEvidenceItemsForField({ evidence, evidenceGroups }, 'food').length > 0 &&
          data.groceriesScore &&
          getEvidenceItemsForField({ evidence, evidenceGroups }, 'groceries').length > 0 &&
          data.drinkingWaterScore &&
          getEvidenceItemsForField({ evidence, evidenceGroups }, 'drinkingWater').length > 0;
      case 'utilities':
        return data.powerCreditScore && data.environmentalSanitationScore;
      case 'observations':
        return data.otherObservations.trim().length > 10 && data.generalComments.trim().length > 10;
      case 'expenses':
        return true;
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const allRequiredComplete = STEPS.slice(0, -1).every((_, index) => isStepComplete(index));
  const canProceed = isStepComplete(currentStep);
  const progressPercent = ((currentStep + 1) / STEPS.length) * 100;

  const handleNext = () => {
    if (currentStep < STEPS.length - 1 && canProceed) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const startVisit = async () => {
    setStarting(true);
    setError('');
    try {
      const gps = await getCurrentPosition(true);
      const response = await authApi.patch(`/visits/${visitId}/start`, gps);
      setVisit(response.data);
      upsertVisit(response.data);
      toast.success('GPS check-in complete. Visit started.');
    } catch (err) {
      const message = getApiErrorMessage(err, err.message || 'Unable to start visit.');
      setError(message);
      toast.error(message);
    } finally {
      setStarting(false);
    }
  };

  const buildChecklistPayload = () => ({
    vitals: {
      body_temperature: {
        value: toNumber(formData.vitals.bodyTemperature),
        unit: 'C',
      },
      blood_pressure: {
        systolic: toNumber(formData.vitals.bloodPressureSystolic),
        diastolic: toNumber(formData.vitals.bloodPressureDiastolic),
      },
      physical_appearance: {
        score: formData.vitals.physicalAppearance,
      },
    },
    physical_activity: {
      ability_to_stand: {
        assistance_required: formData.activity.abilityToStandAssistance,
        score: formData.activity.abilityToStandScore,
      },
      ability_to_walk: {
        assistance_required: formData.activity.abilityToWalkAssistance,
        score: formData.activity.abilityToWalkScore,
      },
      speech: {
        score: formData.activity.speechScore,
      },
    },
    sustenance: {
      food: {
        score: formData.sustenance.foodScore,
      },
      groceries: {
        score: formData.sustenance.groceriesScore,
      },
      drinking_water: {
        score: formData.sustenance.drinkingWaterScore,
      },
    },
    utilities: {
      power_credit: {
        score: formData.utilities.powerCreditScore,
      },
      environmental_sanitation: {
        score: formData.utilities.environmentalSanitationScore,
      },
    },
    other_observations: formData.observations.otherObservations,
    general_comments: formData.observations.generalComments,
  });

  const visitStatus = visit?.status;
  const isCorrectionMode = visitStatus === 'Flagged' || searchParams.get('mode') === 'correction';

  const handleSubmit = async () => {
    if (!allRequiredComplete) {
      toast.error('Complete every required section before submitting.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      if (isCorrectionMode) {
        if (!correctionResponse.trim()) {
          toast.error('Add a correction response for the supervisor.');
          setSubmitting(false);
          return;
        }
        const response = await authApi.patch(`/visits/${visitId}/correction`, {
          checklist: buildChecklistPayload(),
          response: correctionResponse.trim(),
          care_expenses: careExpenses
            .filter(item => item.title?.trim() && item.explanation?.trim())
            .map(({ id, fromSuggestion, ...item }) => item),
        });
        upsertVisit(response.data);
        toast.success('Corrected report sent back to supervisor review.');
        navigate('/visits?status=Submitted');
        return;
      }
      const gps = await getCurrentPosition(true);
      const response = await authApi.patch(`/visits/${visitId}/submit`, {
        checklist: buildChecklistPayload(),
        gps,
        care_expenses: careExpenses
          .filter(item => item.title?.trim() && item.explanation?.trim())
          .map(({ id, fromSuggestion, ...item }) => item),
      });
      upsertVisit(response.data);
      toast.success('Visit submitted to supervisor.');
      navigate('/schedule');
    } catch (err) {
      const message = getApiErrorMessage(err, err.message || 'Unable to submit visit.');
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderScoreSelector = (label, idPrefix, value, onChange, options = DEFAULT_SCORE_OPTIONS) => (
    <div className="space-y-2">
      {label && <Label className="text-sm font-medium">{label}</Label>}
      <RadioGroup value={value} onValueChange={onChange} className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
        {options.map(option => {
          const optionId = `${idPrefix}-${option.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
          return (
            <div key={option} className="flex items-center">
              <RadioGroupItem value={option} id={optionId} className="sr-only" />
              <Label
                htmlFor={optionId}
                className={cn(
                  'w-full cursor-pointer rounded-lg border px-4 py-3 text-center text-sm transition-all sm:w-auto sm:py-2',
                  value === option
                    ? option === 'Great' || option === 'Strong' || option === 'Good' || option === 'Adequate'
                      ? 'border-green-500 bg-green-100 text-green-700'
                      : option === 'Fair'
                        ? 'border-yellow-500 bg-yellow-100 text-yellow-700'
                        : 'border-red-500 bg-red-100 text-red-700'
                    : 'border-border bg-white hover:border-primary/50'
                )}
              >
                {option}
              </Label>
            </div>
          );
        })}
      </RadioGroup>
    </div>
  );

  const renderStepContent = () => {
    switch (STEPS[currentStep].id) {
      case 'vitals':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
              <div className="space-y-2">
                <Label htmlFor="temp">Body Temperature (C) *</Label>
                <Input
                  id="temp"
                  type="number"
                  step="0.1"
                  inputMode="decimal"
                  placeholder="e.g., 36.5"
                  value={formData.vitals.bodyTemperature}
                  onChange={(event) => updateFormData('vitals', 'bodyTemperature', event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Blood Pressure (mmHg) *</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    inputMode="numeric"
                    placeholder="Systolic"
                    value={formData.vitals.bloodPressureSystolic}
                    onChange={(event) => updateFormData('vitals', 'bloodPressureSystolic', event.target.value)}
                  />
                  <span className="self-center text-muted-foreground">/</span>
                  <Input
                    type="number"
                    inputMode="numeric"
                    placeholder="Diastolic"
                    value={formData.vitals.bloodPressureDiastolic}
                    onChange={(event) => updateFormData('vitals', 'bloodPressureDiastolic', event.target.value)}
                  />
                </div>
              </div>
            </div>

            {renderScoreSelector(
              'Physical Appearance *',
              'physical-appearance',
              formData.vitals.physicalAppearance,
              (value) => updateFormData('vitals', 'physicalAppearance', value),
              scoreOptions.wellness || DEFAULT_SCORE_OPTIONS
            )}

            <EvidenceCapture
              authApi={authApi}
              visitId={visitId}
              requirement={physicalRequirement}
              evidence={evidence.physicalCondition}
              evidenceItems={evidenceGroups.physicalCondition}
              onUploaded={handleEvidenceUploaded}
            />
          </div>
        );

      case 'activity':
        return (
          <div className="space-y-6">
            <div className="rounded-xl bg-muted/50 p-4">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <span className="font-medium">Ability to Stand Up</span>
                <div className="flex items-center gap-2">
                  <Label htmlFor="stand-assist" className="text-sm text-muted-foreground">Requires Assistance</Label>
                  <Switch
                    id="stand-assist"
                    checked={formData.activity.abilityToStandAssistance}
                    onCheckedChange={(value) => updateFormData('activity', 'abilityToStandAssistance', value)}
                  />
                </div>
              </div>
              {renderScoreSelector(
                '',
                'ability-to-stand',
                formData.activity.abilityToStandScore,
                (value) => updateFormData('activity', 'abilityToStandScore', value),
                scoreOptions.wellness || DEFAULT_SCORE_OPTIONS
              )}
            </div>

            <div className="rounded-xl bg-muted/50 p-4">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <span className="font-medium">Ability to Walk</span>
                <div className="flex items-center gap-2">
                  <Label htmlFor="walk-assist" className="text-sm text-muted-foreground">Requires Assistance</Label>
                  <Switch
                    id="walk-assist"
                    checked={formData.activity.abilityToWalkAssistance}
                    onCheckedChange={(value) => updateFormData('activity', 'abilityToWalkAssistance', value)}
                  />
                </div>
              </div>
              {renderScoreSelector(
                '',
                'ability-to-walk',
                formData.activity.abilityToWalkScore,
                (value) => updateFormData('activity', 'abilityToWalkScore', value),
                scoreOptions.wellness || DEFAULT_SCORE_OPTIONS
              )}
            </div>

            {renderScoreSelector(
              'Speech Quality *',
              'speech-quality',
              formData.activity.speechScore,
              (value) => updateFormData('activity', 'speechScore', value),
              scoreOptions.speech || DEFAULT_SPEECH_OPTIONS
            )}
          </div>
        );

      case 'sustenance':
        return (
          <div className="space-y-6">
            <div className="space-y-4 rounded-xl border p-4">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-primary" />
                <span className="font-medium">Food Availability</span>
              </div>
              {renderScoreSelector('', 'food-score', formData.sustenance.foodScore, (value) => updateFormData('sustenance', 'foodScore', value), scoreOptions.sustenance || DEFAULT_SUSTENANCE_OPTIONS)}
              <EvidenceCapture authApi={authApi} visitId={visitId} requirement={foodRequirement} evidence={evidence.food} evidenceItems={evidenceGroups.food} onUploaded={handleEvidenceUploaded} />
            </div>

            <div className="space-y-4 rounded-xl border p-4">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-primary" />
                <span className="font-medium">Groceries</span>
              </div>
              {renderScoreSelector('', 'groceries-score', formData.sustenance.groceriesScore, (value) => updateFormData('sustenance', 'groceriesScore', value), scoreOptions.sustenance || DEFAULT_SUSTENANCE_OPTIONS)}
              <EvidenceCapture authApi={authApi} visitId={visitId} requirement={groceriesRequirement} evidence={evidence.groceries} evidenceItems={evidenceGroups.groceries} onUploaded={handleEvidenceUploaded} />
            </div>

            <div className="space-y-4 rounded-xl border p-4">
              <div className="flex items-center gap-2">
                <Droplets className="h-5 w-5 text-blue-500" />
                <span className="font-medium">Drinking Water</span>
              </div>
              {renderScoreSelector('', 'drinking-water-score', formData.sustenance.drinkingWaterScore, (value) => updateFormData('sustenance', 'drinkingWaterScore', value), scoreOptions.water || DEFAULT_WATER_OPTIONS)}
              <EvidenceCapture authApi={authApi} visitId={visitId} requirement={drinkingWaterRequirement} evidence={evidence.drinkingWater} evidenceItems={evidenceGroups.drinkingWater} onUploaded={handleEvidenceUploaded} />
            </div>
          </div>
        );

      case 'utilities':
        return (
          <div className="space-y-6">
            <div className="space-y-4 rounded-xl border p-4">
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-yellow-500" />
                <span className="font-medium">Power Credit</span>
              </div>
              {renderScoreSelector('', 'power-credit-score', formData.utilities.powerCreditScore, (value) => updateFormData('utilities', 'powerCreditScore', value), scoreOptions.power || DEFAULT_POWER_OPTIONS)}
            </div>

            <div className="space-y-4 rounded-xl border p-4">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-green-500" />
                <span className="font-medium">Environmental Sanitation</span>
              </div>
              {renderScoreSelector('', 'environmental-sanitation-score', formData.utilities.environmentalSanitationScore, (value) => updateFormData('utilities', 'environmentalSanitationScore', value), scoreOptions.sanitation || DEFAULT_SANITATION_OPTIONS)}
            </div>
          </div>
        );

      case 'observations':
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="other-obs">Other Observations *</Label>
              <Textarea
                id="other-obs"
                placeholder="Document any other observations about the dependant's condition, environment, or circumstances..."
                className="min-h-[150px]"
                value={formData.observations.otherObservations}
                onChange={(event) => updateFormData('observations', 'otherObservations', event.target.value)}
              />
              <p className="text-xs text-muted-foreground">Minimum 10 characters required</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="general-comments">General Comments *</Label>
              <Textarea
                id="general-comments"
                placeholder="Provide your overall assessment and any recommendations..."
                className="min-h-[150px]"
                value={formData.observations.generalComments}
                onChange={(event) => updateFormData('observations', 'generalComments', event.target.value)}
              />
              <p className="text-xs text-muted-foreground">Minimum 10 characters required</p>
            </div>
          </div>
        );

      case 'expenses':
        return (
          <div className="space-y-5">
            <div className="rounded-xl border border-[#E8E6E3] bg-[#FAFAF7] p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="font-serif text-xl font-medium text-[#1A1A1A]">Care expenses</h3>
                  <p className="text-sm text-[#5A5A5A]">
                    Add supplies or support the subscriber should approve after supervisor review.
                  </p>
                </div>
                <Button type="button" variant="outline" onClick={addCareExpense}>
                  <Plus className="h-4 w-4" />
                  Add expense
                </Button>
              </div>
              {suggestedExpenses.length > 0 && (
                <p className="mt-3 text-sm text-amber-800">
                  {suggestedExpenses.length} suggestion{suggestedExpenses.length === 1 ? '' : 's'} came from checklist items marked inadequate or requiring top-up.
                </p>
              )}
            </div>

            {careExpenses.map((expense, index) => (
              <div key={expense.id} className="space-y-4 rounded-xl border border-[#E8E6E3] bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-[#1A1A1A]">Expense {index + 1}</p>
                    {expense.sourceLabel && (
                      <p className="text-xs text-[#5A5A5A]">
                        Suggested from {expense.sourceLabel}: {expense.triggerValue}
                      </p>
                    )}
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => removeCareExpense(expense.id)}>
                    <Trash2 className="h-4 w-4" />
                    Remove
                  </Button>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input value={expense.title || ''} onChange={(event) => updateCareExpense(expense.id, 'title', event.target.value)} placeholder="e.g., Grocery restock" />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <select
                      value={expense.category || 'Other'}
                      onChange={(event) => updateCareExpense(expense.id, 'category', event.target.value)}
                      className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    >
                      {['Food', 'Groceries', 'Water', 'Power', 'Sanitation', 'Medication', 'Other'].map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Estimated Amount</Label>
                    <Input type="number" min="0" step="0.01" value={expense.estimatedAmount || ''} onChange={(event) => updateCareExpense(expense.id, 'estimatedAmount', event.target.value)} placeholder="Optional" />
                  </div>
                  <div className="space-y-2">
                    <Label>Urgency</Label>
                    <select
                      value={expense.urgency || 'Normal'}
                      onChange={(event) => updateCareExpense(expense.id, 'urgency', event.target.value)}
                      className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    >
                      <option value="Normal">Normal</option>
                      <option value="Urgent">Urgent</option>
                    </select>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Explanation</Label>
                    <Textarea
                      value={expense.explanation || ''}
                      onChange={(event) => updateCareExpense(expense.id, 'explanation', event.target.value)}
                      placeholder="Explain what needs to be bought or handled and why."
                    />
                  </div>
                </div>
              </div>
            ))}

            {careExpenses.length === 0 && (
              <div className="rounded-xl border border-dashed border-[#D8D5D0] bg-[#FAFAF7] p-6 text-center text-sm text-[#5A5A5A]">
                No care expenses added for this visit.
              </div>
            )}
          </div>
        );

      case 'review':
        return (
          <div className="space-y-6">
            <div className={cn(
              'rounded-xl border p-4',
              allRequiredComplete ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'
            )}>
              <div className="mb-2 flex items-center gap-2">
                {allRequiredComplete ? <Check className="h-5 w-5 text-green-600" /> : <AlertTriangle className="h-5 w-5 text-yellow-600" />}
                <span className={cn('font-medium', allRequiredComplete ? 'text-green-800' : 'text-yellow-800')}>
                  {allRequiredComplete ? 'All sections complete' : 'Some required sections are incomplete'}
                </span>
              </div>
              <p className={cn('text-sm', allRequiredComplete ? 'text-green-700' : 'text-yellow-700')}>
                {isCorrectionMode ? 'Your corrected report will return to supervisor review.' : 'GPS checkout is required when you submit this report.'}
              </p>
            </div>

            {isCorrectionMode && (
              <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <Label htmlFor="correction-response" className="text-sm font-medium text-amber-950">
                  Correction response for supervisor *
                </Label>
                <Textarea
                  id="correction-response"
                  value={correctionResponse}
                  onChange={(event) => setCorrectionResponse(event.target.value)}
                  rows={5}
                  className="bg-white"
                  placeholder="Summarize what you corrected and respond to the supervisor's flag..."
                />
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <ThermometerSun className="h-4 w-4" />
                    Vitals
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                  <p>Temperature: {formData.vitals.bodyTemperature || '-'} C</p>
                  <p>BP: {formData.vitals.bloodPressureSystolic || '-'}/{formData.vitals.bloodPressureDiastolic || '-'}</p>
                  <p>Appearance: <Badge variant="outline">{formData.vitals.physicalAppearance || '-'}</Badge></p>
                  <p>Evidence: <Badge variant="outline">{evidence.physicalCondition ? physicalRequirement.evidenceType : 'Missing'}</Badge></p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <Activity className="h-4 w-4" />
                    Physical Activity
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                  <p>Stand: <Badge variant="outline">{formData.activity.abilityToStandScore || '-'}</Badge></p>
                  <p>Walk: <Badge variant="outline">{formData.activity.abilityToWalkScore || '-'}</Badge></p>
                  <p>Speech: <Badge variant="outline">{formData.activity.speechScore || '-'}</Badge></p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <ShoppingCart className="h-4 w-4" />
                    Sustenance
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                  <p>Food: <Badge variant="outline">{formData.sustenance.foodScore || '-'}</Badge></p>
                  <p>Groceries: <Badge variant="outline">{formData.sustenance.groceriesScore || '-'}</Badge></p>
                  <p>Water: <Badge variant="outline">{formData.sustenance.drinkingWaterScore || '-'}</Badge></p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <Zap className="h-4 w-4" />
                    Utilities
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                  <p>Power: <Badge variant="outline">{formData.utilities.powerCreditScore || '-'}</Badge></p>
                  <p>Sanitation: <Badge variant="outline">{formData.utilities.environmentalSanitationScore || '-'}</Badge></p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <Wallet className="h-4 w-4" />
                    Care Expenses
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                  <p>{careExpenses.filter(item => item.title?.trim() && item.explanation?.trim()).length} expense request(s) ready</p>
                  <p className="text-muted-foreground">Subscriber approval opens after supervisor approval.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const isStarted = isCorrectionMode || hasGpsCheckIn(visit);
  const isClosed = visit && visitStatus !== 'Scheduled' && !isCorrectionMode;
  const packageName = schema?.package?.name || subscriber?.plan || 'Unknown';

  const headerSummary = useMemo(() => {
    const dependantName = visit?.dependantName || `${dependant?.firstName || ''} ${dependant?.lastName || ''}`.trim() || 'Dependant';
    return {
      dependantName,
      address: getVisitAddress(visit, dependant),
      packageName,
    };
  }, [dependant, packageName, visit]);

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-6 px-3 pb-24 sm:px-0" data-testid="visit-checklist-wizard">
        <Card>
          <CardContent className="flex items-center gap-3 p-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading visit checklist...
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !visit) {
    return (
      <div className="mx-auto max-w-4xl space-y-6 px-3 pb-24 sm:px-0" data-testid="visit-checklist-wizard">
        <Card>
          <CardContent className="space-y-4 p-8">
            <div className="flex items-start gap-3 text-destructive">
              <AlertTriangle className="mt-0.5 h-5 w-5" />
              <p>{error}</p>
            </div>
            <Button type="button" variant="outline" onClick={() => navigate('/schedule')}>Back to Schedule</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isClosed) {
    return (
      <div className="mx-auto max-w-4xl space-y-6 px-3 pb-24 sm:px-0" data-testid="visit-checklist-wizard">
        <Card>
          <CardContent className="space-y-4 p-8">
            <Badge variant="outline">{visitStatus}</Badge>
            <h1 className="font-heading text-2xl font-bold text-foreground">This visit has already been submitted.</h1>
            <p className="text-muted-foreground">Submitted visits can be reviewed from the visit reports workflow.</p>
            <Button type="button" variant="outline" onClick={() => navigate('/schedule')}>Back to Schedule</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isStarted) {
    return (
      <div className="mx-auto max-w-4xl space-y-6 px-3 pb-24 sm:px-0" data-testid="visit-checklist-wizard">
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-2xl">Start Visit</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="rounded-xl border bg-muted/30 p-4">
              <h2 className="font-semibold text-foreground">{headerSummary.dependantName}</h2>
              <p className="mt-2 flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0" />
                {headerSummary.address}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">Package: <span className="font-medium text-foreground">{headerSummary.packageName}</span></p>
              <p className="mt-1 text-sm text-muted-foreground">
                Physical condition evidence: <span className="font-medium text-foreground">{physicalRequirement.evidenceType}</span>
              </p>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              GPS check-in is required before the checklist opens. Please allow location access on this device.
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
              <Button type="button" variant="outline" onClick={() => navigate('/schedule')}>Back to Schedule</Button>
              <Button type="button" onClick={startVisit} disabled={starting}>
                {starting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MapPin className="mr-2 h-4 w-4" />}
                Start with GPS Check-in
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-5 px-3 pb-28 sm:px-0 md:space-y-6" data-testid="visit-checklist-wizard">
      <div className="space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground md:text-3xl">{isCorrectionMode ? 'Correct Flagged Visit Report' : 'Visit Assessment Checklist'}</h1>
            <p className="mt-1 text-muted-foreground">{headerSummary.dependantName} · {headerSummary.packageName} package</p>
          </div>
          <Badge variant="outline" className="w-fit">{physicalRequirement.evidenceType === 'video' ? 'Video evidence required' : 'Photo evidence required'}</Badge>
        </div>
        <p className="flex items-start gap-2 text-sm text-muted-foreground">
          <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0" />
          {headerSummary.address}
        </p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">Step {currentStep + 1} of {STEPS.length}</span>
          <span className="text-muted-foreground">{Math.round(progressPercent)}% complete</span>
        </div>
        <Progress value={progressPercent} className="h-2" />
      </div>

      <div className="-mx-3 overflow-x-auto px-3">
        <div className="flex min-w-max justify-between gap-3 md:min-w-0">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isComplete = isStepComplete(index);
            const isCurrent = index === currentStep;
            const isPast = index < currentStep;

            return (
              <button
                type="button"
                key={step.id}
                onClick={() => index <= currentStep || isComplete ? setCurrentStep(index) : null}
                className={cn(
                  'flex min-w-[56px] flex-col items-center gap-1 text-center',
                  isCurrent ? 'text-primary' : isPast ? 'text-green-600' : 'text-muted-foreground'
                )}
              >
                <span className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all',
                  isCurrent ? 'border-primary bg-primary/10' :
                    isPast || isComplete ? 'border-green-500 bg-green-100' : 'border-border'
                )}>
                  {isPast || (isComplete && !isCurrent) ? <Check className="h-5 w-5 text-green-600" /> : <Icon className="h-5 w-5" />}
                </span>
                <span className="hidden text-xs font-medium md:block">{step.title}</span>
              </button>
            );
          })}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {React.createElement(STEPS[currentStep].icon, { className: 'h-5 w-5' })}
            {STEPS[currentStep].title}
          </CardTitle>
        </CardHeader>
        <CardContent>{renderStepContent()}</CardContent>
      </Card>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-white/95 p-3 backdrop-blur md:static md:border-0 md:bg-transparent md:p-0 md:backdrop-blur-0">
        <div className="mx-auto flex max-w-4xl justify-between gap-3">
          <Button type="button" variant="outline" onClick={handlePrevious} disabled={currentStep === 0} data-testid="wizard-prev">
            <ChevronLeft className="mr-2 h-4 w-4" />
            Previous
          </Button>

          {currentStep === STEPS.length - 1 ? (
            <Button type="button" onClick={handleSubmit} disabled={!allRequiredComplete || submitting} data-testid="wizard-submit">
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              {isCorrectionMode ? 'Submit Correction' : 'Submit'}
            </Button>
          ) : (
            <Button type="button" onClick={handleNext} disabled={!canProceed} data-testid="wizard-next">
              Next
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={loadChecklist}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <RefreshCcw className="h-4 w-4" />
        Refresh visit data
      </button>
    </div>
  );
};

export default VisitChecklistWizard;
